{
  "title": "New server",
  "intro": "Choose where the server runs, which system it starts with and how much it needs. The summary shows what you chose and what it costs. The setup is kept in the page address, so you can share the link or come back to it later.",
  "create": "Create server",
  "createMany": "Create servers: {{count}}",
  "createShort": "Create",
  "blocked": "To create, fix: {{sections}}.",
  "started": "Creating server “{{name}}”. This usually takes less than a minute.",
  "startedMany": "Creating servers: {{count}}. This usually takes less than a minute.",
  "partial": "Not created: {{names}}. {{reason}}",
  "perHour": "per hour",
  "perMonth": "per month",
  "free": "{{names}} (free)",
  "arch": {
    "x86": "x86 (Intel, AMD)",
    "arm": "Arm64 (Ampere)"
  },
  "region": {
    "title": "Region",
    "intro": "Where the server physically runs. Closer to your users means faster answers. The region cannot be changed later, only by creating the server again.",
    "zone": "Network zone {{zone}}",
    "offered": "server types: {{count}}",
    "typeMissing": "Type {{type}} is not offered here"
  },
  "image": {
    "title": "Image",
    "intro": "What the server starts with: an operating system or a ready-made app. Showing images for {{arch}}.",
    "os": "Operating systems",
    "apps": "Apps",
    "version": "{{os}} version",
    "noApps": "The provider offers no ready-made apps for this architecture right now. Choose an operating system and install what you need yourself or with cloud-init.",
    "appOn": "Based on {{os}}",
    "rapidDeployHint": "The provider keeps this image prepared, so a server starts from it faster.",
    "unknown": "This image is not in the project's catalogue. Choose another one.",
    "wrongArch": "“{{image}}” has no version for {{arch}}. Choose another system, or a server type of the other architecture.",
    "own": "Snapshots and backups",
    "noOwn": "The project has no snapshots or backups. Take one on a server’s page.",
    "ownSnapshot": "Snapshot · {{os}} · {{arch}} · disk from {{disk}}\u00a0GB · {{date}}",
    "ownBackup": "Backup · {{os}} · {{arch}} · disk from {{disk}}\u00a0GB · {{date}}",
    "ownGone": "This copy no longer exists. Choose another one or an operating system.",
    "ownNotReady": "The copy “{{name}}” is still being created.",
    "ownTooLarge": "The copy needs a disk of {{disk}}\u00a0GB or more; {{type}} has {{typeDisk}}\u00a0GB. Choose a type with a larger disk."
  },
  "type": {
    "title": "Server type",
    "intro": "How many processors, how much memory and disk the server gets. Billed by the hour, but never more than the monthly amount. You can move to a bigger type later.",
    "hint": "Shared vCPUs share physical cores with neighbours and cost less; dedicated ones are reserved for your server, for steady load. Traffic is outgoing; incoming is free.",
    "sort": "Sort",
    "sorts": {
      "price": "Cheapest first",
      "cores": "By vCPUs",
      "memory": "By memory",
      "disk": "By disk"
    },
    "noneHere": "No types match these filters in {{region}}. Change the architecture, family or region.",
    "missing": "Not offered in {{region}}: {{types}}. Choose another region to use them.",
    "value": "{{type}}: {{cores}}\u00a0vCPU, {{memory}}\u00a0GB RAM, {{disk}}\u00a0GB",
    "memoryValue": "{{gb}}\u00a0GB RAM",
    "diskValue": "{{gb}}\u00a0GB"
  },
  "networking": {
    "title": "Networking",
    "intro": "Public addresses that make the server reachable from the internet. You can add or remove them later while the server is off.",
    "hint": "The addresses are created with the server and by default deleted with it. You can keep them and assign them elsewhere on the IP addresses page.",
    "ipv4": "Public IPv4",
    "ipv4Price": "{{price}} per month, billed separately from the server.",
    "ipv4Paid": "Billed separately from the server.",
    "ipv6": "Public IPv6",
    "ipv6Free": "A /64 network, free.",
    "ipv6Only": "IPv6 only: some sites and services (some package mirrors, for example) are not reachable over IPv6, and you cannot connect to the server from a network without IPv6.",
    "none": "Without public addresses and without a private network nothing can reach the server. Keep IPv4 or IPv6, or choose a private network below.",
    "noneShort": "No public addresses",
    "privateOnly": "No public addresses: the server is reachable only from the chosen private networks, and it cannot reach the internet itself (for package updates, say) unless a server in the network forwards its traffic."
  },
  "networks": {
    "title": "Private networks",
    "intro": "Servers in one private network talk to each other on internal addresses, away from the internet. Optional, free of charge.",
    "hint": "A network lives in network zones. A server can join it when the network has a subnet in the zone of the server's region; the server gets an address from that subnet.",
    "subnet": "{{range}}: address from {{subnet}} ({{zone}})",
    "otherZone": "No subnet in {{zone}}, the zone of this region. Add one on the network's page, or choose a region in another zone.",
    "empty": "The project has no private networks yet.",
    "noneFits": "None of the project's networks has a subnet in {{zone}}.",
    "create": "Create a network",
    "none": "None"
  },
  "firewalls": {
    "title": "Firewalls",
    "intro": "Rules for what traffic may reach the server, in force from its first start. Optional, free of charge.",
    "hint": "A firewall only allows: once one is applied, traffic its rules do not allow is dropped. The rules of several firewalls add up.",
    "rules": "Inbound rules: {{in}}, outbound: {{out}}",
    "empty": "The project has no firewalls yet.",
    "open": "No firewall chosen: every port of the server is open to the internet. You can apply one later too.",
    "create": "Create a firewall",
    "none": "None, all ports open"
  },
  "keys": {
    "title": "SSH keys",
    "intro": "Keys that may sign in to the server as root. Safer than a password: a key cannot be guessed.",
    "none": "No key chosen: the provider generates a root password. We show it once, right after creating, and keep it nowhere.",
    "passwordShort": "Root password, shown once"
  },
  "backups": {
    "title": "Backups",
    "intro": "Once a day the provider copies the server's whole disk and keeps the last seven copies. A copy can restore the server or start a new one.",
    "enable": "Turn on daily backups",
    "price": "+{{percent}}\u00a0% of the server price: {{price}} per month.",
    "percent": "+{{percent}}\u00a0% of the server price.",
    "on": "Daily",
    "off": "Off"
  },
  "terminal": {
    "title": "Browser terminal",
    "intro": "From its first boot the server trusts your organisation's SSH certificate authority: the panel's terminal signs in with one-time certificates, without your keys or passwords.",
    "enable": "Browser terminal",
    "hint": "Adds a few lines to cloud-init: the authority's key in /etc/ssh and a TrustedUserCAKeys line in the sshd settings. Your SSH keys and the root password work as before.",
    "on": "On",
    "off": "Off"
  },
  "labels": {
    "title": "Labels",
    "intro": "Key–value pairs to find and group servers, e.g. env=production. Optional.",
    "hint": "Key and value up to 63 characters: Latin letters and digits at both ends, also “-”, “_” and “.” inside. The value may be empty.",
    "key": "Key",
    "value": "Value",
    "add": "Add label",
    "remove": "Remove label {{key}}",
    "none": "None",
    "problems": {
      "keyMissing": "Enter a key.",
      "keyInvalid": "Key: up to 63 characters, Latin letters and digits at both ends, also “-”, “_” and “.” inside.",
      "valueInvalid": "Value: up to 63 characters, Latin letters and digits at both ends, also “-”, “_” and “.” inside.",
      "duplicate": "This key is already used."
    }
  },
  "autopilot": {
    "title": "Autopilot",
    "intro": "A monitoring agent installs itself on the server's first boot: metrics, services, containers and warnings show on the server's page. Free.",
    "enable": "Turn on Autopilot",
    "enableHint": "CloudServer adds the agent install to the server's cloud-init. The agent only looks and warns: it changes and updates nothing on the server.",
    "on": "On (free)",
    "onNoContainers": "On, without containers (free)",
    "off": "Off",
    "role": "Agent role",
    "roleHint": "What the AI assistant may do on the server besides monitoring."
  },
  "userData": {
    "title": "Cloud-init",
    "intro": "A script or configuration the server runs on its first start: install packages, add users, write files. Optional.",
    "label": "User data",
    "hint": "{{used}} of {{max}}\u00a0KB used. Start with #cloud-config or with a #!/bin/bash line.",
    "why": "Cloud-init reads this only on the first start. Keep passwords out of it: the data can be read on the server itself.",
    "tooLarge": "Larger than 32\u00a0KB, which the provider does not accept. Shorten it, or fetch big files from a script.",
    "set": "Set",
    "none": "None"
  },
  "name": {
    "title": "Name",
    "intro": "The server's name in the project; it also becomes the system's hostname. We suggest one from the system, type and region.",
    "label": "Server name",
    "baseLabel": "Start of the names",
    "hint": "Latin letters, digits and hyphens, dot-separated parts allowed (like a hostname). No spaces or underscores.",
    "why": "The name must be unique in the project. It shows in the server list and in the shell prompt.",
    "useSuggested": "Use the suggestion: {{name}}",
    "preview": "Will be created: {{names}}",
    "problems": {
      "invalid": "Not a valid name: Latin letters, digits and hyphens only, no hyphen at the start or end of a part.",
      "tooLong": "Too long: at most 253 characters including the number.",
      "taken": "A server with this name already exists in the project."
    }
  },
  "count": {
    "title": "Count",
    "intro": "How many identical servers to create, up to {{max}} at once. The names get -1, -2 and so on.",
    "label": "Number of servers",
    "less": "Fewer",
    "more": "More",
    "value": "Servers: {{count}}"
  },
  "summary": {
    "title": "Summary",
    "open": "Summary",
    "server": "Server",
    "ipv4": "Public IPv4",
    "backups": "Backups",
    "times": "Per server, number of servers: {{count}}",
    "total": "Total",
    "perMonth": "{{price}} per month",
    "hourly": "{{price}} per hour",
    "billing": "Prices without VAT, {{currency}}. Billed by the hour from creation, never more than the monthly amount. A server that is off is billed too.",
    "states": {
      "ok": "Done",
      "warn": "Note",
      "error": "Needs fixing"
    },
    "totalPerMonth": "Total per month"
  },
  "password": {
    "title": "Root password",
    "titleMany": "Root passwords",
    "lead": "No SSH key was chosen, so the provider generated a password to sign in: ssh root@<server address>.",
    "label": "Root password of {{name}}",
    "copy": "Copy the password of {{name}}",
    "copied": "Password copied",
    "once": "Save the password now, in a password manager for example. CloudServer does not keep it and cannot show it again; if it is lost, reset it on the server page.",
    "saved": "I saved the password",
    "done": "Done, go to the server"
  },
  "backToDashboard": "Back to the dashboard",
  "volumes": {
    "title": "Volumes",
    "intro": "Free volumes of this location can be attached from the start. They are billed already; the server price does not change. Optional.",
    "hint": "A volume attaches to one server of its location. New volumes are created on the Volumes page.",
    "none": "No volumes",
    "line": "{{size}}\u00a0GB · {{format}}",
    "noFs": "no FS",
    "empty": "No free volumes in {{region}}.",
    "oneServer": "A volume attaches to one server: remove the volumes or create one server.",
    "automount": "Mount automatically",
    "automountHint": "On Hetzner's system images volumes with a file system appear at /mnt/HC_Volume_<number>.",
    "create": "Create volume"
  },
  "placement": {
    "title": "Placement group",
    "intro": "Servers of one group run on different physical hosts. Optional, free.",
    "hint": "A server created in a group gets its own host right away; an existing server joins only while powered off.",
    "none": "No group",
    "noneHint": "The server goes on any fitting host.",
    "members": "Servers: {{count}} of {{max}}",
    "full": "No room for {{count}} more",
    "create": "Create group"
  }
}
